/**
 * Assembles an export bundle from the two storage layers.
 *
 * Layer 1 — renderer `localStorage` zustand `persist` stores. These have no
 * file on disk the main process could read, so collection MUST happen here.
 * Layer 2 — main-process MetaInfo records, fetched over `metainfo:list-all`.
 *
 * Both feed one function so there is exactly one place where a bundle is
 * born, and one place where `assertNoDeniedData` runs before the bytes ever
 * reach the main process. Sources are injected so tests can seed every store
 * (including the deny-listed ones) with sentinels.
 */
import {
  SETTINGS_BUNDLE_GROUPS,
  groupById,
  type ExportedField,
  type SettingsBundleGroup,
  type SettingsBundleGroupId,
} from './settingsBundleManifest';
import { assertNoDeniedData } from './settingsBundleGuard';
import {
  SETTINGS_BUNDLE_KIND,
  SETTINGS_BUNDLE_VERSION,
  type GroupPayload,
  type SettingsBundle,
  type StoreSlicePayload,
} from './settingsBundleTypes';

export type BundleSources = {
  /** Returns a store's persisted state object, or null when it has none. */
  readStore: (storeKey: string) => Record<string, unknown> | null;
  /** `metainfo:list-all`. Only called when the user opted into MetaInfo. */
  listMetaInfo?: () => Promise<Array<Record<string, unknown>>>;
};

function isPlainish(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Copies exactly the declared fields out of one store's persisted state.
 *
 * THROWS when a field's value is an object/array and the manifest declared
 * neither a nested `fields` allowlist nor a `deep` reason. That is the
 * mechanical guarantee behind "nothing rides along inside an allowlisted
 * object": adding a field to an exported object is fine, but making the
 * *object itself* exportable requires writing down why.
 */
export function collectStoreSlice(
  state: Record<string, unknown>,
  fields: ExportedField[],
): StoreSlicePayload {
  const out: StoreSlicePayload = {};
  for (const field of fields) {
    if (!(field.name in state)) continue;
    const value = state[field.name];
    if (value === undefined) continue;

    if (!isPlainish(value)) {
      out[field.name] = value;
      continue;
    }
    if (field.deep) {
      // Round-tripped so only JSON-representable data can travel.
      out[field.name] = JSON.parse(JSON.stringify(value));
      continue;
    }
    if (!field.fields || Array.isArray(value)) {
      throw new Error(
        `Settings export blocked — "${field.name}" holds an object, so the manifest ` +
          'must declare either a nested `fields` allowlist or a written `deep` reason.',
      );
    }
    const nested = collectStoreSlice(value, field.fields);
    if (Object.keys(nested).length > 0) out[field.name] = nested;
  }
  return out;
}

/** A `metainfo:list-all` entry that is genuinely a per-database record. */
function isDatabaseMetaInfoRecord(record: unknown): record is Record<string, unknown> {
  if (!isPlainish(record) || Array.isArray(record)) return false;
  return (
    typeof record.connectionId === 'string' &&
    typeof record.database === 'string' &&
    Array.isArray(record.looseRelationships)
  );
}

export async function buildSettingsBundle(args: {
  groupIds: readonly SettingsBundleGroupId[];
  includeMetaInfo?: boolean;
  sources: BundleSources;
  appVersion?: string;
  now?: () => Date;
  /** Injected only by tests, to prove a widened allowlist is still refused. */
  groups?: readonly SettingsBundleGroup[];
}): Promise<SettingsBundle> {
  const catalog = args.groups ?? SETTINGS_BUNDLE_GROUPS;
  const groups: SettingsBundle['groups'] = {};

  for (const groupId of args.groupIds) {
    const group = groupById(groupId, catalog);
    if (!group) continue;
    const payload: GroupPayload = {};
    for (const slice of group.slices) {
      const state = args.sources.readStore(slice.store);
      if (!state) continue;
      const collected = collectStoreSlice(state, slice.fields);
      if (Object.keys(collected).length > 0) payload[slice.store] = collected;
    }
    if (Object.keys(payload).length > 0) groups[groupId] = payload;
  }

  const bundle: SettingsBundle = {
    kind: SETTINGS_BUNDLE_KIND,
    version: SETTINGS_BUNDLE_VERSION,
    exportedAt: (args.now?.() ?? new Date()).toISOString(),
    ...(args.appVersion ? { appVersion: args.appVersion } : {}),
    groups,
  };

  if (args.includeMetaInfo && args.sources.listMetaInfo) {
    const all = await args.sources.listMetaInfo();
    // Filter, then let the guard assert nothing survived that should not
    // have. `metainfo:list-all` walks every *.json under a connection
    // directory, so the Object-Explorer pins file can appear here.
    bundle.metaInfo = (Array.isArray(all) ? all : []).filter(isDatabaseMetaInfoRecord);
  }

  assertNoDeniedData(bundle, catalog);
  return bundle;
}
